import {
  createLearnerCommentApi,
  deleteLearnerCommentApi,
  getLearnerCommentsApi,
  updateLearnerCommentApi,
} from "@/api/services/main/learning-progress";
import { TLearnerCommentDto } from "@/api/services/main/learning-progress/dtos/learner-comment.dto";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { background, divider, black, primary } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { typography } from "@/theme/typography";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import { Stack, Box, Typography, IconButton, Paper, TextField, styled, CircularProgress } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

type TCommentsSectionProps = {
  learnerId: number | string;
};

const ROLE_COLORS: Record<string, { bg: string; text: string }> = {
  "先生": { bg: "#DCEDC8", text: "#2E3436" },
  "登録支援": { bg: "#B2DFDB", text: "#2E3436" },
  "所属機関": { bg: "#E1BEE7", text: "#2E3436" },
};

const PER_PAGE = 10;
const MAX_COMMENT_LENGTH = 1000;

interface CommentBubbleProps {
  isOwn?: boolean;
  isDeleted?: boolean;
}

const CommentBubble = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "isOwn" && prop !== "isDeleted",
})<CommentBubbleProps>(({ isOwn, isDeleted }) => ({
  backgroundColor: isOwn ? primary[400] : "#ECF9F9",
  color: isOwn ? "#FFFFFF" : "#2E3436",
  padding: "8px",
  borderRadius: rounded.sm,
  width: "100%",
  fontFamily: "inherit",
  boxShadow: "none",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  whiteSpace: "pre-wrap",
  ...typography.body14,
  ...(isDeleted && { fontStyle: "italic", opacity: 0.7 }),
}));

const RoleLabel = styled(Box)<{ bgColor: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 76px;
  padding: 4px 8px;
  background-color: ${({ bgColor }) => bgColor};
  border-radius: ${rounded.md};
`;

const CommentItem = styled(Stack)`
  & .comment-actions {
    opacity: 0;
    transition: opacity 0.15s ease-in-out;
  }
  &:hover .comment-actions,
  &:focus-within .comment-actions {
    opacity: 1;
  }
`;

export const CommentsSection: FC<TCommentsSectionProps> = ({ learnerId }) => {
  const queryClient = useQueryClient();

  const {
    dataFormatter,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteScroll<TLearnerCommentDto>(
    [QUERY_KEYS.LEARNING_PROGRESS.FETCH_LEARNER_COMMENTS, learnerId],
    (page) => getLearnerCommentsApi({ learnerId, page, perPage: PER_PAGE }),
  );

  // API returns newest-first across pages → reverse for chat order (oldest top, newest bottom)
  const comments = useMemo(() => [...dataFormatter].reverse(), [dataFormatter]);

  const [inputText, setInputText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const topSentinelRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledToBottomRef = useRef(false);
  const prevScrollHeightRef = useRef<number | null>(null);

  // Stable ref to latest query state — lets the observer read current values
  // without re-attaching on every render.
  const loadMoreStateRef = useRef({ hasNextPage, isFetching, fetchNextPage });
  loadMoreStateRef.current = { hasNextPage, isFetching, fetchNextPage };

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  // Initial scroll to bottom once comments are first loaded
  useEffect(() => {
    if (!hasScrolledToBottomRef.current && !isLoading && comments.length > 0) {
      scrollToBottom();
      hasScrolledToBottomRef.current = true;
    }
  }, [isLoading, comments.length]);

  // After fetching older page: restore scroll position so viewport stays put
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || prevScrollHeightRef.current === null) return;
    el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
    prevScrollHeightRef.current = null;
  }, [comments]);

  // Load older page when top sentinel enters viewport (more robust than scroll math).
  // Runs once per mount; observer reads latest state via ref.
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const { hasNextPage: hasMore, isFetching: fetching, fetchNextPage: fetchMore } =
          loadMoreStateRef.current;
        if (entries[0]?.isIntersecting && hasMore && !fetching) {
          prevScrollHeightRef.current = root.scrollHeight;
          fetchMore();
        }
      },
      { root, threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const invalidateAndScrollBottom = async () => {
    hasScrolledToBottomRef.current = false;
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.LEARNING_PROGRESS.FETCH_LEARNER_COMMENTS, learnerId],
    });
  };

  const handleSubmit = async () => {
    const text = inputText.trim();
    if (!text || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await createLearnerCommentApi(learnerId, { text });
      setInputText("");
      await invalidateAndScrollBottom();
    } catch (e) {
      showError(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartEdit = (comment: TLearnerCommentDto) => {
    setEditingId(comment.id);
    setEditingText(comment.text);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };

  const handleSaveEdit = async (commentId: number) => {
    const text = editingText.trim();
    if (!text || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await updateLearnerCommentApi(learnerId, commentId, { text });
      toast.success(API_SUCCESS_MESSAGE);
      handleCancelEdit();
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.LEARNING_PROGRESS.FETCH_LEARNER_COMMENTS, learnerId],
      });
    } catch (e) {
      showError(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (deletingId === null || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await deleteLearnerCommentApi(learnerId, deletingId);
      toast.success(API_SUCCESS_MESSAGE);
      setDeletingId(null);
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.LEARNING_PROGRESS.FETCH_LEARNER_COMMENTS, learnerId],
      });
    } catch (e) {
      showError(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${divider.high}`,
        borderRadius: rounded.sm,
        backgroundColor: background.white,
        minHeight: 0,
      }}
    >
      {/* Header */}
      <Stack px={3} pt={3} pb={1} sx={{ borderBottom: `1px solid ${divider.middle}` }}>
        <Typography variant="sub16Semi" color={black[30]}>
          コメント
        </Typography>
      </Stack>

      {/* Comments list */}
      <Stack
        ref={scrollRef}
        spacing={2}
        px={3}
        py={2}
        sx={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Box ref={topSentinelRef} sx={{ height: 1 }} />

        {isFetchingNextPage && (
          <Stack alignItems="center" py={1}>
            <CircularProgress size={20} />
          </Stack>
        )}

        {isLoading ? (
          <Stack alignItems="center" py={2}>
            <CircularProgress size={24} />
          </Stack>
        ) : (
          comments.map((comment, index) => {
            const roleColor = ROLE_COLORS[comment.authorRole] || ROLE_COLORS["先生"];
            const isEditing = editingId === comment.id;
            const prev = comments[index - 1];
            const isSameAuthorAsPrev =
              !!prev &&
              prev.isOwn === comment.isOwn &&
              prev.authorName === comment.authorName &&
              prev.authorRole === comment.authorRole;
            return (
              <CommentItem
                key={comment.id}
                gap={1}
                alignItems={comment.isOwn ? "flex-end" : "flex-start"}
              >
                {!isSameAuthorAsPrev && (
                  <Stack direction="row" alignItems="center" gap={1}>
                    <RoleLabel bgColor={roleColor.bg}>
                      <Typography variant="cap12" color={roleColor.text}>
                        {comment.authorRole}
                      </Typography>
                    </RoleLabel>
                    <Typography variant="body14Semi" color={black[70]}>
                      {comment.authorName}
                    </Typography>
                  </Stack>
                )}

                {isEditing ? (
                  <TextField
                    fullWidth
                    multiline
                    autoFocus
                    variant="outlined"
                    size="small"
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    inputProps={{ maxLength: MAX_COMMENT_LENGTH }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: rounded.sm,
                        fontSize: 14,
                        backgroundColor: background.white,
                        "&.Mui-focused fieldset": { borderColor: primary[400] },
                      },
                    }}
                  />
                ) : (
                  <CommentBubble elevation={0} isOwn={comment.isOwn} isDeleted={comment.isDeleted}>
                    {comment.text}
                  </CommentBubble>
                )}

                <Stack direction="row" alignItems="center" gap={1} alignSelf="stretch">
                  <Typography variant="cap12" color={black[90]}>
                    {comment.date}
                  </Typography>
                  {comment.isOwn && !comment.isDeleted && (
                    <Stack
                      direction="row"
                      gap={1}
                      sx={{ ml: "auto" }}
                      className={isEditing ? undefined : "comment-actions"}
                    >
                      {isEditing ? (
                        <>
                          <IconButton
                            size="small"
                            disabled={!editingText.trim() || isSubmitting}
                            onClick={() => handleSaveEdit(comment.id)}
                            sx={{ color: primary[400], p: 0 }}
                          >
                            <CheckIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={handleCancelEdit}
                            sx={{ color: black[80], p: 0 }}
                          >
                            <CloseIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleStartEdit(comment)}
                            sx={{ color: black[80], p: 0 }}
                          >
                            <EditIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => setDeletingId(comment.id)}
                            sx={{ color: "#E57373", p: 0 }}
                          >
                            <DeleteIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        </>
                      )}
                    </Stack>
                  )}
                </Stack>
              </CommentItem>
            );
          })
        )}
      </Stack>

      {/* Input area */}
      <Stack
        direction="row"
        gap={1}
        px={3}
        pb={3}
        alignItems="center"
        sx={{ borderTop: `1px solid ${divider.high}`, pt: 2, mt: "auto" }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={4}
          variant="outlined"
          size="small"
          placeholder="入力してください"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          inputProps={{ maxLength: MAX_COMMENT_LENGTH }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: rounded.sm,
              fontSize: 14,
              "&.Mui-focused fieldset": {
                borderColor: primary[400],
              },
            },
          }}
        />
        <IconButton
          size="small"
          disabled={!inputText.trim() || isSubmitting}
          onClick={handleSubmit}
          sx={{
            color: primary[400],
            border: `1px solid ${divider.middle}`,
            borderRadius: rounded.sm,
            p: "6px",
          }}
        >
          <SendIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Stack>

      <ConfirmDeleteModal
        open={deletingId !== null}
        onClose={() => setDeletingId(null)}
        onOk={handleConfirmDelete}
        message="このコメントを削除しますか？"
      />
    </Stack>
  );
};
